import {
  Banknote,
  Boxes,
  Building2,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FileBadge,
  FileText,
  Gauge,
  GraduationCap,
  HandCoins,
  HeartPulse,
  IdCard,
  LayoutDashboard,
  Megaphone,
  PiggyBank,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Timer,
  UserCog,
  UserMinus,
  UserPlus,
  Users,
  UsersRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/**
 * Navigation.
 *
 * One declaration drives the sidebar, the command palette, the Quick Create
 * menu and the route guards. A page that exists but is unreachable, or a link
 * that leads somewhere the user cannot open, both come from having more than
 * one list.
 *
 * `permissions` is an *any-of* list: holding one of them is enough to see the
 * item. An empty list means everyone signed in to the web app sees it.
 */

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  permissions: string[];
  /** Shown as a count badge when the loader supplies one. */
  badgeKey?: 'approvals' | 'attention' | 'flagged';
  description: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      {
        label: 'HR Overview',
        to: '/',
        icon: LayoutDashboard,
        permissions: [],
        description: 'What needs attention today across the whole company.',
      },
      {
        label: 'Approvals',
        to: '/approvals',
        icon: ClipboardCheck,
        permissions: ['approval.view', 'approval.decide', 'leave.approve_hr'],
        badgeKey: 'approvals',
        description: 'Every request waiting on a decision, in one queue.',
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        label: 'Employees',
        to: '/employees',
        icon: Users,
        permissions: ['employee.view_all', 'team.view'],
        description: 'The employee directory and every employee record.',
      },
      {
        label: 'Departments',
        to: '/departments',
        icon: Building2,
        permissions: ['organization.manage', 'employee.view_all'],
        description: 'Departments, teams, positions and job levels.',
      },
      {
        label: 'Work Locations',
        to: '/work-locations',
        icon: IdCard,
        permissions: ['work_location.manage', 'organization.manage'],
        description: 'Offices, branches, field sites and their geofences.',
      },
    ],
  },
  {
    label: 'Time and Attendance',
    items: [
      {
        label: 'Attendance',
        to: '/attendance',
        icon: Clock,
        permissions: ['attendance.review', 'team.attendance.view'],
        description: 'Live attendance, daily records and the day view.',
      },
      {
        label: 'Attendance Review',
        to: '/attendance/review',
        icon: ShieldCheck,
        permissions: ['attendance.review'],
        badgeKey: 'flagged',
        description: 'Attendance evidence that needs a human decision.',
      },
      {
        label: 'Corrections',
        to: '/attendance/corrections',
        icon: ClipboardList,
        permissions: ['attendance.correction.approve', 'attendance.review'],
        description: 'Correction requests and the adjustments they produce.',
      },
      {
        label: 'Devices',
        to: '/attendance/devices',
        icon: Smartphone,
        permissions: ['attendance.review'],
        description: 'Phones waiting for approval to record attendance.',
      },
      {
        label: 'Schedules',
        to: '/schedules',
        icon: CalendarClock,
        permissions: ['schedule.assign', 'shift.manage'],
        description: 'Shift templates and the schedule planner.',
      },
      {
        label: 'Timesheets',
        to: '/timesheets',
        icon: Timer,
        permissions: ['payroll.run', 'attendance.review'],
        description: 'Period totals and payroll readiness.',
      },
      {
        label: 'Leave',
        to: '/leave',
        icon: CalendarDays,
        permissions: ['leave.approve_hr', 'leave.balance.manage', 'approval.view'],
        description: 'Leave requests, balances and the leave calendar.',
      },
      {
        label: 'Leave Balances',
        to: '/leave/balances',
        icon: CalendarDays,
        permissions: ['leave.balance.manage', 'leave.approve_hr'],
        description: 'Balances, manual adjustments and the leave types behind them.',
      },
      {
        label: 'Overtime',
        to: '/overtime',
        icon: Gauge,
        permissions: ['overtime.approve_hr', 'approval.view'],
        description: 'Overtime requests and payroll readiness.',
      },
    ],
  },
  {
    label: 'Payroll and Compensation',
    items: [
      {
        label: 'Payroll',
        to: '/payroll',
        icon: Wallet,
        permissions: ['payroll.run', 'payroll.approve'],
        description: 'Payroll periods, calculations and approval.',
      },
      {
        label: 'Payslips',
        to: '/payroll/payslips',
        icon: Receipt,
        permissions: ['payslip.publish', 'payroll.run'],
        description: 'Publication, acknowledgement and republication.',
      },
      {
        label: 'Payroll Configuration',
        to: '/payroll/configuration',
        icon: Banknote,
        permissions: ['statutory_table.manage'],
        description: 'Contribution and tax tables, versioned by effective date.',
      },
      {
        label: 'Compensation',
        to: '/compensation',
        icon: HandCoins,
        permissions: ['compensation.view', 'compensation.manage'],
        description: 'Effective-dated salary records and history.',
      },
      {
        label: 'Benefits',
        to: '/benefits',
        icon: HeartPulse,
        permissions: ['benefits.view', 'benefits.manage'],
        description: 'Plans, enrollments and dependents.',
      },
      {
        label: 'Loans',
        to: '/loans',
        icon: PiggyBank,
        permissions: ['loans.view', 'loans.manage'],
        description: 'Payroll-deduction records and balances.',
      },
    ],
  },
  {
    label: 'Talent',
    items: [
      {
        label: 'Recruitment',
        to: '/recruitment',
        icon: Sparkles,
        permissions: ['recruitment.view', 'recruitment.manage'],
        description: 'Requisitions, pipeline and offers.',
      },
      {
        label: 'Candidates',
        to: '/recruitment/candidates',
        icon: UserPlus,
        permissions: ['recruitment.view', 'recruitment.manage'],
        description: 'The candidate pipeline, by stage.',
      },
      {
        label: 'Onboarding',
        to: '/onboarding',
        icon: ClipboardCheck,
        permissions: ['onboarding.manage'],
        description: 'New-hire cases and their tasks.',
      },
      {
        label: 'Probation',
        to: '/probation',
        icon: FileBadge,
        permissions: ['probation.decide', 'onboarding.manage'],
        description: 'Probation reviews and decisions.',
      },
      {
        label: 'Performance',
        to: '/performance',
        icon: UsersRound,
        permissions: ['performance.manage'],
        description: 'Review cycles, goals and ratings.',
      },
      {
        label: 'Learning',
        to: '/learning',
        icon: GraduationCap,
        permissions: ['learning.manage'],
        description: 'Courses, sessions and completion records.',
      },
    ],
  },
  {
    label: 'Employee Operations',
    items: [
      {
        label: 'Documents',
        to: '/documents',
        icon: FileText,
        permissions: ['document.manage'],
        description: 'Employee documents, expiry and verification.',
      },
      {
        label: 'Policies',
        to: '/policies',
        icon: ScrollText,
        permissions: ['policy.publish', 'document.manage'],
        description: 'Policy versions and acknowledgement progress.',
      },
      {
        label: 'Announcements',
        to: '/announcements',
        icon: Megaphone,
        permissions: ['announcement.publish'],
        description: 'Company announcements and scheduling.',
      },
      {
        label: 'Assets',
        to: '/assets',
        icon: Boxes,
        permissions: ['asset.manage'],
        description: 'Inventory, assignment and returns.',
      },
      {
        label: 'Employee Relations',
        to: '/employee-relations',
        icon: FileBadge,
        permissions: ['employee_relations.view'],
        description: 'Confidential cases. Restricted access.',
      },
      {
        label: 'Offboarding',
        to: '/offboarding',
        icon: UserMinus,
        permissions: ['offboarding.manage'],
        description: 'Separation cases and clearance.',
      },
    ],
  },
  {
    label: 'Analytics and Governance',
    items: [
      {
        label: 'Reports',
        to: '/reports',
        icon: ClipboardList,
        permissions: ['report.view'],
        description: 'Workforce, attendance, leave and payroll reporting.',
      },
      {
        label: 'Audit Log',
        to: '/audit',
        icon: ScrollText,
        permissions: ['audit_log.view'],
        description: 'Every recorded action. Append-only.',
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        label: 'System Users',
        to: '/users',
        icon: UserCog,
        permissions: ['system.administer'],
        description: 'Accounts, roles and access.',
      },
      {
        label: 'Roles and Permissions',
        to: '/roles',
        icon: ShieldCheck,
        permissions: ['system.administer'],
        description: 'What each role can do.',
      },
      {
        label: 'Company Settings',
        to: '/settings',
        icon: Settings,
        permissions: ['system.administer'],
        description: 'Company profile, policies and configuration.',
      },
    ],
  },
];

/** Every navigable item, flattened — used by the command palette. */
export const ALL_NAV_ITEMS: NavItem[] = NAVIGATION.flatMap((group) => group.items);

/** The permissions guarding a path, for the route guard. */
export function permissionsForPath(path: string): string[] {
  const exact = ALL_NAV_ITEMS.find((item) => item.to === path);
  if (exact) return exact.permissions;
  const prefix = ALL_NAV_ITEMS.filter((item) => item.to !== '/' && path.startsWith(item.to)).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];
  return prefix?.permissions ?? [];
}

/** Whether somebody holding these permissions may open a path at all. */
export function canOpenPath(path: string, granted: readonly string[]): boolean {
  const required = permissionsForPath(path);
  return required.length === 0 || required.some((permission) => granted.includes(permission));
}

/* -------------------------------------------------------------------------- */
/* Quick Create                                                                */
/* -------------------------------------------------------------------------- */

export interface QuickCreateAction {
  label: string;
  to: string;
  icon: LucideIcon;
  permissions: string[];
}

export const QUICK_CREATE: QuickCreateAction[] = [
  { label: 'Add employee', to: '/employees/new', icon: UserPlus, permissions: ['employee.manage'] },
  {
    label: 'Add department',
    to: '/departments?create=department',
    icon: Building2,
    permissions: ['organization.manage'],
  },
  {
    label: 'Add shift',
    to: '/schedules?create=shift',
    icon: CalendarClock,
    permissions: ['shift.manage'],
  },
  {
    label: 'Assign schedule',
    to: '/schedules?create=assignment',
    icon: CalendarDays,
    permissions: ['schedule.assign'],
  },
  {
    label: 'Create payroll period',
    to: '/payroll?create=run',
    icon: Wallet,
    permissions: ['payroll.run'],
  },
  {
    label: 'Add candidate',
    to: '/recruitment/candidates?create=candidate',
    icon: UserPlus,
    permissions: ['recruitment.manage'],
  },
  {
    label: 'Publish announcement',
    to: '/announcements?create=announcement',
    icon: Megaphone,
    permissions: ['announcement.publish'],
  },
  {
    label: 'Add policy',
    to: '/policies?create=policy',
    icon: ScrollText,
    permissions: ['policy.publish'],
  },
  {
    label: 'Register asset',
    to: '/assets?create=asset',
    icon: Boxes,
    permissions: ['asset.manage'],
  },
  {
    label: 'Start offboarding',
    to: '/offboarding?create=case',
    icon: UserMinus,
    permissions: ['offboarding.manage'],
  },
];
